import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';

import { removeOneUserFromStore, loadUsersToStore } from 'store/actions/user';
import { setPageTitle } from 'store/actions/header';
import { useUserService } from 'hooks/api/iam/user';
import { truncateText } from 'app/shared/utils/general';
import GmModal from 'app/shared/modal/modal';
import { EarningPercentageUpdationForm } from '../components/modify-earnings';
import { Badge } from '@arco-design/web-react';

export const UserListPage = () => {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const { deactivateUser, fetchUsers, searchUsers } = useUserService();

  const { is_mobile_view } = useSelector((state) => state.metadata);
  const users_in_store = useSelector((state) => state.users);

  const [users, setUsers] = useState([]);
  const [user_to_modify, setUserToModify] = useState({});
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show_earning_modal, setShowEarningModal] = useState(false);
  const [total_users, setTotalUsers] = useState(0);

  useEffect(() => {
    dispatch(
      setPageTitle([
        { title: 'User Management', path: '..' },
        { title: 'Users', path: '.' }
      ])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUsers(Object.values(users_in_store));
  }, [users_in_store]);

  const config = {
    actions: {
      single: () => [
        [
          { label: 'Deactivate', value: 'deactivate' },
          { label: 'Update Earnings %', value: 'update_earning_percent' }
        ]
      ]
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'ID',
        key: 'id',
        isTitle: true,
        formatter: (value) => value
      },
      {
        title: 'First name',
        key: 'firstname',
        isTitle: true,
        searchable: true,
        formatter: (value) => truncateText(value, 40)
      },
      {
        title: 'Last name',
        key: 'lastname',
        isTitle: true,
        searchable: true,
        formatter: (value) => truncateText(value, 40)
      },
      {
        title: 'Email Address',
        key: 'email',
        isTagline: true,
        searchable: true,
        formatter: (value) => truncateText(value, 40)
      },
      {
        title: 'Account type',
        key: 'account_type',
        isTagline: true,
        formatter: (value) => (
          <Badge text={value || 'regular'} color={value === 'affiliate' ? 'green' : 'grey'} />
        )
      },
      {
        title: 'Status',
        key: 'is_active',
        isTagline: true,
        formatter: (value) => (value ? 'Active' : 'Inactive')
      },
      {
        title: 'Modified On',
        key: 'updated_on',
        formatter: (value) => new Date(value).toDateString(),
        isMetadata: true
      }
    ],
    is_search_mode,
    items: users.sort((a, b) => b.id - a.id),
    search_key: 'name',
    search_text: ''
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;

    if (type === 'single') {
      switch (name) {
        case 'deactivate':
          handleDeactivation(data.id);
          break;
        case 'update_earning_percent':
          setUserToModify(data);
          setShowEarningModal(true);
          break;
        default:
          navigateTo(`/users/${data.id}`);
      }
    }
  };

  const handleDeactivation = async (id) => {
    const { user } = await deactivateUser(id);
    if (user) {
      dispatch(removeOneUserFromStore(id));
    }
  };

  const handleItemClick = (payload) => {
    const { id } = payload;
    navigateTo(`/users/${id}`);
  };

  const handleDataRequest = async (page, population = 10) => {
    try {
      setLoading(true);
      const { users, size } = await fetchUsers({
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });

      dispatch(loadUsersToStore(users));
      setTotalUsers(size);
    } catch (e) {
      dispatch(loadUsersToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (keys, keyword, page, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { users, size } = await searchUsers(keys, keyword, {
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });

      setTotalUsers(size);
      dispatch(loadUsersToStore(users));
    } catch (e) {
      dispatch(loadUsersToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const table_actions = <></>;

  return (
    <div>
      {is_mobile_view ? (
        <MobileDatatable
          config={config}
          action={handleDatatableAction}
          onClick={handleItemClick}
          onListModeChange={setSearchMode}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
        />
      ) : (
        <WebDatatable
          config={{
            ...config,
            total_count: total_users
          }}
          action={handleDatatableAction}
          onClick={handleItemClick}
          checkbox
          loading_data={loading}
          table_actions={table_actions}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
        />
      )}
      <GmModal
        show_modal={show_earning_modal}
        show_title
        onClose={() => setShowEarningModal(false)}
        title="Update Earning Percentage"
      >
        <EarningPercentageUpdationForm data={user_to_modify} />
      </GmModal>
    </div>
  );
};
